import Button from 'components/app-components/ui/button';
import LoadingSpinner from 'icons/LoadingSpinner';
import { useEffect, useRef, useState } from 'react';
import { createRenamedFile, isValidFileName, replaceExtraDotsWithDash, sanitizeFileName } from 'utils/files';
import { FileUploadProps } from './type';

function generateFileTypeString(acceptedFileTypes: string[]): string {
  // Check for document file types
  const hasWord = acceptedFileTypes.some(
    (type) =>
      type === 'application/msword' ||
      type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  );
  const hasExcel = acceptedFileTypes.some(
    (type) =>
      type === 'application/vnd.ms-excel' ||
      type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
  const hasXml = acceptedFileTypes.some((type) => type === 'application/xml' || type === 'text/xml');
  const hasCsv = acceptedFileTypes.includes('text/csv');
  const hasTxt = acceptedFileTypes.includes('text/plain');
  const hasPdf = acceptedFileTypes.includes('application/pdf');
  const hasImages = acceptedFileTypes.some(
    (type) => type === 'image/jpeg' || type === 'image/jpg' || type === 'image/png',
  );

  // If we have multiple document types
  const documentTypes = [];
  if (hasPdf) documentTypes.push('PDF');
  if (hasWord) documentTypes.push('Word');
  if (hasExcel) documentTypes.push('Excel');
  if (hasXml) documentTypes.push('XML');
  if (hasCsv) documentTypes.push('CSV');
  if (hasTxt) documentTypes.push('TXT');

  if (documentTypes.length > 0 && hasImages) {
    return `Images and ${documentTypes.join(', ')} files`;
  } else if (documentTypes.length > 0) {
    return `${documentTypes.join(', ')} files`;
  } else if (hasImages) {
    return 'Image files only';
  } else {
    return 'All file types';
  }
}

export const FileUpload = ({
  label,
  file,
  setFile,
  labelClassName,
  uploadAreaIcon,
  uploadAreaText,
  maxSizeInMb = 10,
  maxTotalSizeInMb,
  handleFileUpload,
  error,
  isUploading,
  setUploadErrorMessage,
  defaultValue,
  setIsRemovedFile,
  setContractFileID,
  acceptedFileTypes = ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'],
  onChangeHandler,
  uploadAreaClassName,
  uploadAreaLabel,
  hasMultipleUploads = false,
  files = [],
  setFiles = () => {},
  fileIDs = [],
  setFileIDs = () => {},
  hideFileTypeLabel = false,
}: FileUploadProps) => {
  const MAX_TOTAL_SIZE_IN_BYTES = maxTotalSizeInMb ? maxTotalSizeInMb * 1000000 : undefined;
  const MAX_FILE_SIZE_IN_BYTES = maxSizeInMb * 1000000;
  const RenderPropsIcon = uploadAreaIcon;
  const [dragActive, setDragActive] = useState(false);
  const acceptedFileTypeLabel = generateFileTypeString(acceptedFileTypes);

  const fileInputRef = useRef(null);

  // Track total committed size via ref so async handleFileUpload callers
  // can't race past the limit before the files prop updates.
  const pendingTotalSizeRef = useRef(0);
  useEffect(() => {
    // Sync ref with the authoritative files prop (handles removals, resets, etc.)
    pendingTotalSizeRef.current = files.reduce((sum, f) => sum + f.size, 0);
  }, [files]);

  const getValidatedFile = (file: File): File | null => {
    if (file.size > MAX_FILE_SIZE_IN_BYTES) {
      setUploadErrorMessage(`File size is greater than maximum limit of ${maxSizeInMb}MB`);
      return null;
    }

    if (acceptedFileTypes && acceptedFileTypes?.length > 0 && !acceptedFileTypes.includes(file.type)) {
      setUploadErrorMessage('File type is not supported');
      return null;
    }

    // Sanitize filename: convert accented characters to ASCII equivalents (e.g., accented names -> ASCII names)
    const sanitizedFileName = sanitizeFileName(file.name);

    // If file name has multiple dots, normalize to a single extension separator before validating/uploading.
    const normalizedFileName = replaceExtraDotsWithDash(sanitizedFileName);
    if (!isValidFileName(normalizedFileName)) {
      setUploadErrorMessage('File name is not valid. Please remove special characters from the file name.');
      return null;
    }

    if (normalizedFileName !== file.name) {
      return createRenamedFile(file, normalizedFileName);
    }

    return file;
  };

  const checkTotalSizeLimit = (newFiles: File[]): boolean => {
    if (!MAX_TOTAL_SIZE_IN_BYTES) return true;
    const currentTotal = pendingTotalSizeRef.current;
    const newTotal = newFiles.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + newTotal > MAX_TOTAL_SIZE_IN_BYTES) {
      setUploadErrorMessage(`Total file size exceeds ${maxTotalSizeInMb}MB limit. Please upload in smaller batches.`);
      return false;
    }
    // Optimistically reserve the space so a second drop can't race past the limit
    pendingTotalSizeRef.current = currentTotal + newTotal;
    return true;
  };

  const handleDrag = function (e) {
    e.preventDefault();
    e.stopPropagation();
    setUploadErrorMessage('');
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = function (e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setUploadErrorMessage('');

    if (hasMultipleUploads) {
      const newFiles = Array.from(e.dataTransfer.files)
        .map((file: File) => getValidatedFile(file))
        .filter((f): f is File => Boolean(f));
      if (newFiles.length > 0 && checkTotalSizeLimit(newFiles)) {
        newFiles.forEach((file: File) => {
          handleFileUpload(file);
        });
      }
    } else if (e.dataTransfer.files?.[0]) {
      const validatedFile = getValidatedFile(e.dataTransfer.files[0]);
      if (validatedFile) {
        handleFileUpload(validatedFile);
      }
    }
  };

  const handleChange = function (e) {
    e.preventDefault();
    setUploadErrorMessage('');
    onChangeHandler?.(e);

    if (hasMultipleUploads) {
      const newFiles = Array.from(e.target.files)
        .map((file: File) => getValidatedFile(file))
        .filter((f): f is File => Boolean(f));
      if (newFiles.length > 0 && checkTotalSizeLimit(newFiles)) {
        newFiles.forEach((file: File) => {
          handleFileUpload(file);
        });
      }
      e.target.value = '';
    } else if (e.target.files?.[0]) {
      const validatedFile = getValidatedFile(e.target.files[0]);
      if (validatedFile) {
        handleFileUpload(validatedFile);
      }
      e.target.value = '';
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setUploadErrorMessage('');
    setContractFileID?.(null);
  };

  const handleRemoveMultipleFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);

    if (fileIDs) {
      const newFileIDs = [...fileIDs];
      newFileIDs.splice(index, 1);
      setFileIDs(newFileIDs);
    }

    setUploadErrorMessage('');
  };

  const handleDropZoneClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="relative h-full w-full">
      <div className="h-full pb-5">
        <label
          htmlFor="file"
          className={`${error ? 'text-red-600' : 'text-neutral-900'} ${labelClassName} mb-2 block text-sm font-medium`}
        >
          {label}
        </label>
        {hasMultipleUploads ? (
          files.length > 0 ? (
            <div className="space-y-2">
              {files.map((uploadedFile, index) => (
                <div
                  key={index}
                  className={`${
                    error ? 'bg-red-50' : 'bg-neutral-100'
                  } flex w-full items-center justify-between rounded-lg border-0 p-2.5 text-sm text-neutral-900 focus:border focus:border-blue-500 focus:ring-blue-500`}
                >
                  <div className="truncate">{uploadedFile.name}</div>
                  <Button
                    variant="default"
                    onClick={() => handleRemoveMultipleFile(index)}
                    className="px-3 font-semibold text-red-600 hover:text-red-200"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <label
                htmlFor="dropzone-file"
                className={`${
                  dragActive ? 'bg-gray-100' : 'bg-gray-50'
                } relative mt-2 flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 hover:bg-gray-100 ${
                  uploadAreaClassName ?? ''
                }`}
                onDragEnter={handleDrag}
                onClick={handleDropZoneClick}
              >
                {!isUploading ? (
                  <div className="flex flex-col items-center justify-center">
                    {uploadAreaIcon && <RenderPropsIcon />}
                    <p className="mb-2 mt-3 text-sm text-gray-500">
                      <span>{uploadAreaText || 'Add more files'}</span>
                    </p>
                    {uploadAreaLabel}
                    <p className="text-xs text-gray-500">
                      {hideFileTypeLabel ? '' : `${acceptedFileTypeLabel} `}({maxSizeInMb ?? 10}MB MAX)
                    </p>
                  </div>
                ) : (
                  <LoadingSpinner />
                )}
              </label>
            </div>
          ) : defaultValue ? (
            <div
              id="file"
              className={`${
                error ? 'bg-red-50' : 'bg-neutral-100'
              } flex w-full items-center justify-between rounded-lg border-0 p-2.5 text-sm text-neutral-900 focus:border focus:border-blue-500 focus:ring-blue-500`}
            >
              <div className="truncate">{defaultValue}</div>
              <Button
                variant="default"
                onClick={() => setIsRemovedFile(null)}
                className="px-3 font-semibold text-red-600 hover:text-red-200"
              >
                Remove
              </Button>
            </div>
          ) : (
            <label
              htmlFor="dropzone-file"
              className={`${
                dragActive ? 'bg-gray-100' : 'bg-gray-50'
              } relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 hover:bg-gray-100 ${
                uploadAreaClassName ?? ''
              }`}
              onDragEnter={handleDrag}
              onClick={handleDropZoneClick}
            >
              {!isUploading ? (
                <div className="flex flex-col items-center justify-center">
                  {uploadAreaIcon && <RenderPropsIcon />}
                  <p className="mb-2 mt-3 text-sm text-gray-500">
                    <span>{uploadAreaText}</span>
                  </p>
                  {uploadAreaLabel}
                  <p className="text-xs text-gray-500">
                    {hideFileTypeLabel ? '' : `${acceptedFileTypeLabel} `}({maxSizeInMb ?? 10}MB MAX)
                  </p>
                </div>
              ) : (
                <LoadingSpinner />
              )}
            </label>
          )
        ) : file ? (
          <div
            id="file"
            className={`${
              error ? 'bg-red-50' : 'bg-neutral-100'
            } flex w-full items-center justify-between rounded-lg border-0 p-2.5 text-sm text-neutral-900 focus:border focus:border-blue-500 focus:ring-blue-500`}
          >
            <div className="truncate">{file.name}</div>
            <Button
              variant="default"
              onClick={handleRemoveFile}
              className="px-3 font-semibold text-red-600 hover:text-red-800"
            >
              Remove
            </Button>
          </div>
        ) : defaultValue ? (
          <div
            id="file"
            className={`${
              error ? 'bg-red-50' : 'bg-neutral-100'
            } flex w-full items-center justify-between rounded-lg border-0 p-2.5 text-sm text-neutral-900 focus:border focus:border-blue-500 focus:ring-blue-500`}
          >
            <div className="truncate">{defaultValue}</div>
            <Button
              variant="default"
              onClick={() => setIsRemovedFile(null)}
              className="px-3 font-semibold text-red-600 hover:text-red-200"
            >
              Remove
            </Button>
          </div>
        ) : (
          <label
            htmlFor="dropzone-file"
            className={`${
              dragActive ? 'bg-gray-100' : 'bg-gray-50'
            } relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 hover:bg-gray-100 ${
              uploadAreaClassName ?? ''
            }`}
            onDragEnter={handleDrag}
            onClick={handleDropZoneClick}
          >
            {!isUploading ? (
              <div className="flex flex-col items-center justify-center">
                {uploadAreaIcon && <RenderPropsIcon />}
                <p className="mb-2 mt-3 text-sm text-gray-500">
                  <span>{uploadAreaText}</span>
                </p>
                {uploadAreaLabel}
                <p className="text-xs text-gray-500">
                  {hideFileTypeLabel ? '' : `${acceptedFileTypeLabel} `}({maxSizeInMb ?? 10}MB MAX)
                </p>
              </div>
            ) : (
              <LoadingSpinner />
            )}
          </label>
        )}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
      <input
        id="file"
        type="file"
        className="hidden"
        onChange={handleChange}
        ref={fileInputRef}
        required={false}
        accept={acceptedFileTypes.join(',')}
        multiple={hasMultipleUploads}
      />
      {dragActive && (
        <div
          className="absolute bottom-0 left-0 right-0 top-0 h-full w-full"
          id="drag-file-element"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        ></div>
      )}
    </div>
  );
};
